import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Database, RefreshCw, CheckCircle2, PauseCircle, AlertCircle, Table as TableIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import KimKimAddonsBrowser from "./KimKimAddonsBrowser";

const STATUS_META = {
  running: { label: "En marcha", color: "bg-blue-500", icon: Loader2, iconClass: "animate-spin text-blue-600" },
  finished: { label: "Completado", color: "bg-emerald-500", icon: CheckCircle2, iconClass: "text-emerald-600" },
  paused: { label: "Pausado (esperando reanudación)", color: "bg-amber-500", icon: PauseCircle, iconClass: "text-amber-600" },
  not_started: { label: "No iniciado", color: "bg-slate-400", icon: AlertCircle, iconClass: "text-slate-500" },
};

export default function KimKimScrapeWidget() {
  const qc = useQueryClient();
  const [browserOpen, setBrowserOpen] = useState(false);

  const statusQ = useQuery({
    queryKey: ["kimkim-scrape-status"],
    queryFn: async () => (await api.get("/admin/kimkim/scrape/status")).data,
    refetchInterval: 8000, // 8s
    retry: 1,
  });

  const resumeMut = useMutation({
    mutationFn: async () => (await api.post("/admin/kimkim/scrape/resume")).data,
    onSuccess: (data) => {
      toast.success(`Scraper: ${data.action}`);
      qc.invalidateQueries({ queryKey: ["kimkim-scrape-status"] });
    },
    onError: (e) => toast.error(`Error al reanudar: ${e?.message}`),
  });

  const s = statusQ.data;
  if (statusQ.isLoading || !s) return null;
  // Solo mostramos si hay algo interesante (no oculto cuando finished para que confirme)
  if (s.status === "not_started" && s.addons.in_mongo === 0) return null;

  const meta = STATUS_META[s.status] || STATUS_META.not_started;
  const Icon = meta.icon;
  const pagesPct = s.pages.pct || 0;
  const addonsInMongo = s.addons.in_mongo;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="kimkim-scrape-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4 text-slate-600" />
            Importación KimKim Addons
            <Badge variant="secondary" className="ml-1">
              <Icon className={`mr-1 h-3 w-3 ${meta.iconClass}`} />
              {meta.label}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBrowserOpen(true)}
              data-testid="kimkim-open-browser-btn"
            >
              <TableIcon className="mr-1 h-3 w-3" />
              Ver addons ({addonsInMongo.toLocaleString("es-ES")})
            </Button>
            {s.status === "paused" && (
              <Button
                size="sm"
                variant="default"
                onClick={() => resumeMut.mutate()}
                disabled={resumeMut.isPending}
                data-testid="kimkim-scrape-resume-btn"
              >
                <RefreshCw className={`mr-1 h-3 w-3 ${resumeMut.isPending ? "animate-spin" : ""}`} />
                Reanudar
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => statusQ.refetch()}
              data-testid="kimkim-scrape-refresh-btn"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progreso principal */}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="kimkim-scrape-pages">Páginas {s.pages.done}/{s.pages.total}</span>
            <span data-testid="kimkim-scrape-eta">
              {s.status === "running" ? `ETA ${s.eta.human}` : ""}
              {s.status === "finished" ? "✓ Completado" : ""}
              {s.status === "paused" ? "En pausa" : ""}
            </span>
          </div>
          <Progress value={pagesPct} className="h-2" />
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-lg border bg-slate-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Guardados</div>
            <div className="text-lg font-bold tabular-nums" data-testid="kimkim-scrape-in-mongo">
              {addonsInMongo.toLocaleString("es-ES")} <span className="text-xs font-normal text-slate-400">/ {s.addons.expected.toLocaleString("es-ES")}</span>
            </div>
          </div>
          <div className="rounded-lg border bg-emerald-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Con descripción</div>
            <div className="text-lg font-bold tabular-nums text-emerald-900">{s.addons.with_description.toLocaleString("es-ES")}</div>
          </div>
          <div className="rounded-lg border bg-amber-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Con precios</div>
            <div className="text-lg font-bold tabular-nums text-amber-900">{s.addons.with_pricing_table.toLocaleString("es-ES")}</div>
          </div>
          <div className="rounded-lg border bg-indigo-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">Con fotos</div>
            <div className="text-lg font-bold tabular-nums text-indigo-900">{s.addons.with_images.toLocaleString("es-ES")}</div>
          </div>
        </div>

        {/* Types + destinations */}
        {(s.types?.length > 0 || s.top_destinations?.length > 0) && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {s.types?.length > 0 && (
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Tipos</div>
                <div className="flex flex-wrap gap-1.5">
                  {s.types.map((t) => (
                    <Badge key={t.type} variant="outline" className="text-xs" data-testid={`kimkim-type-${t.type}`}>
                      {t.type} · {t.count.toLocaleString("es-ES")}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {s.top_destinations?.length > 0 && (
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Top destinos</div>
                <div className="flex flex-wrap gap-1.5">
                  {s.top_destinations.slice(0, 6).map((d) => (
                    <Badge key={d.destination} variant="outline" className="text-xs">
                      {d.destination} · {d.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Errores */}
        {s.errors.count > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            <strong>{s.errors.count}</strong> errores registrados. El scraper continúa; se pueden reintentar al final.
          </div>
        )}

        {/* Detalle en vivo */}
        {s.log_tail?.length > 0 && s.status === "running" && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none py-1 font-medium">Ver log en vivo (últimas líneas)</summary>
            <pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-950 p-2 text-[10px] leading-relaxed text-slate-100">
              {s.log_tail.slice(-10).join("\n")}
            </pre>
          </details>
        )}
      </CardContent>
      <KimKimAddonsBrowser open={browserOpen} onOpenChange={setBrowserOpen} />
    </Card>
  );
}
